<template>
  <div class="verification-code-block">
    <div id="verification-code">
      <valid-field
        v-for="(val, index) in inputValue"
        :key="index"
        v-slot="{ errors, field }"
        ref="provider"
        :mode="mode"
        :rules="rules"
        tag="div"
        :name="`verification-code-pr-${index}`">
        <input
          :id="`verification-code-${index}`"
          ref="codeInput"
          v-model="inputValue[index]"
          v-qx-input="'onlyNumbers'"
          :class="{ errorInput: errors.length > 0 || isError }"
          class="form-control verification-code-input"
          :max-length="1"
          :autofocus="false"
          :name="`code-${index}`"
          v-bind="field"
          @paste="onPaste()"
          @focus="onFocus(Number([index]))"
          @input="onInput(Number([index]))"
          @keyup.arrow-left="onPressMove('left', Number([index]))"
          @keyup.arrow-right="onPressMove('right', Number([index]))"
          @keyup.backspace="onPressDelete(Number([index]))">
      </valid-field>
    </div>
    <div class="error-wrapper">
      <span
        v-if="isError"
        class="validator-error">
        {{ string.errorMessage }}
      </span>
    </div>
  </div>
</template>
<script>
import { qxInput } from '@/directives/input';
import { Field as ValidField } from 'vee-validate';

export default {
  name: 'VerificationCodeInput',
  components: {
    ValidField,
  },
  directives: {
    qxInput,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    rules: {
      type: [Object, String],
      default: () => ({ required: true, digits: 1 }),
    },
    symbolsCount: {
      type: Number,
      default: 6,
    },
    mode: {
      type: String,
      default: 'validDirty',
      validator: (value) => ['aggressive', 'passive', 'lazy', 'eager', 'validDirty'].includes(value),
    },
    focusOnInit: {
      type: Boolean,
      default: true,
    },
    isError: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      inputValue: [],
      pasted: false,
    };
  },
  computed: {
    string() {
      return {
        errorMessage: this.$gettext('Wrong authentication code'),
      };
    },
  },
  created() {
    this.inputValue = Array(this.symbolsCount).fill('');
  },
  mounted() {
    if (this.focusOnInit) {
      this.$refs.codeInput[0].focus();
    }
  },
  methods: {
    onFocus(index) {
      if (index === 0 || (index !== 0 && this.inputValue.every((val) => val === ''))) {
        this.$refs.codeInput[0].focus();
      }
    },
    onPressDelete(index) {
      if (this.inputValue[index] === '' && index > 0) {
        this.inputValue[index - 1] = '';
        this.$refs.codeInput[index - 1].focus();
      }
    },
    onInput(index) {
      const startValue = this.inputValue[index];
      const valueLength = startValue.length;
      if (valueLength > 1) {
        const firstCharacter = startValue[0];
        this.inputValue[index] = firstCharacter;
        this.$refs.codeInput[index].value = firstCharacter;
        this.$refs.codeInput[index].dispatchEvent(new Event('input'));

        const next = index + 1;
        if (next < this.symbolsCount) {
          this.$refs.codeInput[next].focus();
          this.$refs.codeInput[next].value = startValue.slice(1);
          this.$refs.codeInput[next].dispatchEvent(new Event('input'));
        }
      } else if (/[0-9]/g.test(startValue) && index < this.symbolsCount - 1) {
        this.$refs.codeInput[index + 1].focus();
      }
      this.$emit('update:modelValue', this.inputValue.join(''));
    },
    onPressMove(code, index) {
      if (code === 'left' && index > 0) {
        this.$refs.codeInput[index - 1].focus();
      }
      if (code === 'right' && index < this.symbolsCount - 1) {
        this.$refs.codeInput[index + 1].focus();
      }
    },
    onPaste() {
      this.pasted = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.verification-code-block {
  #verification-code {
    display: flex;
    margin: 0 -7px;
  }
  .verification-code-input {
    width: 45px;
    margin: 0 7px;
  }
  :deep {
    input {
      padding: 13px;
      font-size: 20px;
      font-weight: 700;
      color: $input-gray;
      text-align: center;
      height: 54px;
    }
  }
  .error-wrapper {
    padding-top: 3px;
    height: 15px;
  }
}
</style>
